import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ScrollText, Save, MessageSquare, UserCog, ShieldAlert, Settings2, Volume2, Hash, Search, Ban, X, Plus, History, Download, Paperclip, Bell, Lock, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";

const CATEGORIES = [
  { key: "message", label: "Message logs", icon: MessageSquare, color: "#5865F2", desc: "Deleted, edited & bulk-purged messages." },
  { key: "member", label: "Member logs", icon: UserCog, color: "#f59e0b", desc: "Joins, leaves, nickname/username & role changes." },
  { key: "moderation", label: "Moderation logs", icon: ShieldAlert, color: "#ef4444", desc: "Bans, unbans, kicks & timeouts." },
  { key: "server", label: "Server logs", icon: Settings2, color: "#f59e0b", desc: "Channel & role create / delete / update." },
  { key: "voice", label: "Voice logs", icon: Volume2, color: "#a855f7", desc: "Voice channel joins, leaves & moves." },
];
const CAT_META = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

function ChannelPicker({ linked, channels, value, onChange, placeholder, testid }) {
  if (linked) {
    return (
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt">
        <option value="">{placeholder}</option>
        {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
      </select>
    );
  }
  return (
    <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
  );
}

function IgnoreAdder({ linked, options, values, onChange, labelFor, placeholder, testid }) {
  const [pending, setPending] = useState("");
  const add = (v) => {
    v = (v || "").trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setPending("");
  };
  return (
    <div>
      <div className="flex gap-2">
        {linked ? (
          <select data-testid={`${testid}-select`} value={pending} onChange={(e) => add(e.target.value)}
            className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt">
            <option value="">{placeholder}</option>
            {options.filter((o) => !values.includes(o.id)).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        ) : (
          <>
            <input data-testid={`${testid}-input`} value={pending} onChange={(e) => setPending(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add(pending)} placeholder={placeholder}
              className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
            <button data-testid={`${testid}-add`} onClick={() => add(pending)} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 flex items-center gap-1 text-sm"><Plus className="w-4 h-4" /></button>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {values.map((v) => (
          <span key={v} className="flex items-center gap-1.5 bg-zinc-800 text-zinc-200 rounded-full pl-3 pr-1.5 py-1 text-xs">
            {labelFor(v)}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="hover:bg-zinc-700 rounded-full p-0.5"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {values.length === 0 && <span className="text-xs text-zinc-600">None</span>}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

function HistoryTab({ guildId }) {
  const [logs, setLogs] = useState(null);
  const [counts, setCounts] = useState({});
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get(`/guilds/${guildId}/logs`, { params: { category: cat, q, limit: 150 } })
      .then((r) => { setLogs(r.data.logs); setCounts(r.data.counts || {}); })
      .catch(() => setLogs([]));
  }, [guildId, cat, q]);
  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const exportLogs = async (format) => {
    try {
      const r = await api.get(`/guilds/${guildId}/logs/export`, { params: { format, category: cat }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `logs-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Exported logs as ${format.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search logs…"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:border-volt" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button data-testid="log-filter-all" onClick={() => setCat("all")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${cat === "all" ? "bg-volt text-volt-ink" : "bg-zinc-900 text-zinc-400 hover:text-white"}`}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c.key} data-testid={`log-filter-${c.key}`} onClick={() => setCat(c.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize ${cat === c.key ? "bg-volt text-volt-ink" : "bg-zinc-900 text-zinc-400 hover:text-white"}`}>
              {c.key} {counts[c.key] ? <span className="opacity-60">({counts[c.key]})</span> : null}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <button data-testid="log-refresh" onClick={load} className="px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-900 text-zinc-400 hover:text-white">Refresh</button>
        <div className="flex gap-2">
          <button data-testid="log-export-csv" onClick={() => exportLogs("csv")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white"><Download className="w-3.5 h-3.5" /> CSV</button>
          <button data-testid="log-export-json" onClick={() => exportLogs("json")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white"><Download className="w-3.5 h-3.5" /> JSON</button>
        </div>
      </div>

      {logs === null ? <p className="text-zinc-500 text-sm">Loading…</p> : logs.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <History className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          No log entries yet. Once the bot records events on your live server, they'll appear here.
        </div>
      ) : (
        <div className="space-y-2" data-testid="log-history-list">
          {logs.map((l) => {
            const m = CAT_META[l.category] || {};
            const Icon = m.icon || ScrollText;
            const atts = l.attachments || [];
            return (
              <div key={l.id} data-testid="log-entry" className="flex gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3" style={{ borderLeft: `3px solid ${m.color || "#5865F2"}` }}>
                <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: m.color || "#5865F2" }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{l.title}</span>
                    <span className="text-[11px] text-zinc-500 shrink-0">{timeAgo(l.created_at)}</span>
                  </div>
                  {l.description && <div className="text-xs text-zinc-400 mt-0.5">{l.description}</div>}
                  {(l.fields || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                      {l.fields.map((f, i) => <div key={i} className="text-[11px]"><span className="text-zinc-500">{f.name}: </span><span className="text-zinc-300">{f.value}</span></div>)}
                    </div>
                  )}
                  {atts.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="log-attachments">
                      {atts.map((a, i) => a.is_image ? (
                        <a key={i} href={a.url} target="_blank" rel="noreferrer" title={a.filename}>
                          <img src={a.url} alt={a.filename} className="w-16 h-16 object-cover rounded-md border border-zinc-700" onError={(e) => { e.target.style.display = "none"; }} />
                        </a>
                      ) : (
                        <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[11px] bg-black border border-zinc-700 rounded-md px-2 py-1 text-zinc-300 hover:text-white">
                          <Paperclip className="w-3 h-3" /> {a.filename || "file"}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Logging() {
  const { guildId, refreshKey } = useGuild();
  const [tab, setTab] = useState("settings");
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [linked, setLinked] = useState(false);
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get(`/guilds/${guildId}/modules/logging`).then((r) => setCfg(r.data));
    api.get(`/guilds/${guildId}/discord/channels`).then((r) => { setLinked(r.data.linked); setChannels(r.data.channels || []); }).catch(() => {});
    api.get(`/guilds/${guildId}/discord/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => {});
  }, [guildId, refreshKey]);

  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  const cats = c.categories || {};
  const setCat = (key, patch) => setCfg((prev) => ({ ...prev, config: { ...prev.config, categories: { ...prev.config.categories, [key]: { ...prev.config.categories[key], ...patch } } } }));
  const setField = (k, v) => setCfg((prev) => ({ ...prev, config: { ...prev.config, [k]: v } }));
  const a = c.alerts || {};
  const setAlert = (k, v) => setCfg((prev) => ({ ...prev, config: { ...prev.config, alerts: { ...(prev.config.alerts || {}), [k]: v } } }));

  const saveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/logging`, { enabled: cfg.enabled, config: cfg.config });
    toast.success("Logging settings saved");
  };

  const chName = (id) => (linked ? (channels.find((ch) => ch.id === id)?.name || id) : id);
  const roleName = (id) => (linked ? (roles.find((r) => r.id === id)?.name || id) : id);
  const defaultName = chName(c.default_channel);

  return (
    <div>
      <PageHead title="Server Logs" desc="Send a detailed audit trail of everything happening on your server to Discord channels of your choice.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Enabled</span><Toggle testid="logging-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="logging-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>

      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        {[["settings", "Settings", Settings2], ["history", "History", History]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`logging-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-volt text-volt" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "history" ? <HistoryTab guildId={guildId} /> : (
        <>
          {!linked && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm rounded-lg px-4 py-3 mb-6">
              This server isn't linked to the bot in this environment — type channel/role names manually (without #). On your live server you'll get dropdowns of real channels & roles.
            </div>
          )}

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><ScrollText className="w-4 h-4 text-volt" /> Default log channel</h3>
                <p className="text-xs text-zinc-500 mb-3">Used for any category that doesn't have its own channel set below.</p>
                <ChannelPicker linked={linked} channels={channels} value={c.default_channel} testid="logging-default-channel"
                  onChange={(v) => setField("default_channel", v)} placeholder={linked ? "— select a channel —" : "e.g. server-logs"} />
              </div>
              <div>
                <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><History className="w-4 h-4 text-volt" /> History retention</h3>
                <p className="text-xs text-zinc-500 mb-3">How long in-dashboard log history is kept before auto-deleting.</p>
                <select data-testid="log-retention" value={c.retention_days ?? 30} onChange={(e) => setField("retention_days", +e.target.value)}
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt">
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {CATEGORIES.map((cat) => {
              const state = cats[cat.key] || { enabled: true, channel: "" };
              return (
                <div key={cat.key} data-testid={`log-cat-${cat.key}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-black border border-zinc-800 flex items-center justify-center shrink-0"><cat.icon className="w-4 h-4" style={{ color: cat.color }} /></div>
                      <div>
                        <div className="font-semibold text-sm">{cat.label}</div>
                        <div className="text-xs text-zinc-500">{cat.desc}</div>
                      </div>
                    </div>
                    <Toggle testid={`log-toggle-${cat.key}`} checked={state.enabled !== false} onChange={(v) => setCat(cat.key, { enabled: v })} />
                  </div>
                  <div className={state.enabled === false ? "opacity-40 pointer-events-none" : ""}>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-1"><Hash className="w-3 h-3" /> Channel <span className="text-zinc-600 normal-case tracking-normal">(blank = default)</span></label>
                    <ChannelPicker linked={linked} channels={channels} value={state.channel} testid={`log-channel-${cat.key}`}
                      onChange={(v) => setCat(cat.key, { channel: v })} placeholder={linked ? `— use default${defaultName ? ` (#${defaultName})` : ""} —` : "channel name (optional)"} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6">
            <h3 className="font-head font-semibold flex items-center gap-2 mb-1"><Ban className="w-4 h-4 text-volt" /> Ignore list</h3>
            <p className="text-xs text-zinc-500 mb-4">Anything on this list is excluded from every log category — keep your feed clean.</p>
            <div className="flex items-center justify-between mb-5 pb-5 border-b border-zinc-800">
              <div><div className="text-sm text-zinc-300">Ignore bot accounts</div><div className="text-xs text-zinc-600">Skip actions performed by bots.</div></div>
              <Toggle testid="log-ignore-bots" checked={c.ignore_bots !== false} onChange={(v) => setField("ignore_bots", v)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Ignored channels</label>
                <IgnoreAdder linked={linked} options={channels} values={c.ignored_channels || []} testid="ignore-channels"
                  onChange={(v) => setField("ignored_channels", v)} labelFor={(v) => `#${chName(v)}`} placeholder={linked ? "Add a channel…" : "channel name"} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Ignored roles</label>
                <IgnoreAdder linked={linked} options={roles} values={c.ignored_roles || []} testid="ignore-roles"
                  onChange={(v) => setField("ignored_roles", v)} labelFor={(v) => `@${roleName(v)}`} placeholder={linked ? "Add a role…" : "role name"} />
              </div>
            </div>
          </div>

          {/* Security alerts */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-head font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-volt" /> Security alerts</h3>
              <Toggle testid="log-alerts-enabled" checked={!!(c.alerts && c.alerts.enabled)} onChange={(v) => setAlert("enabled", v)} />
            </div>
            <p className="text-xs text-zinc-500 mb-4">Ping a role the moment a sensitive event fires so your mods can react fast.</p>
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-5 ${!(c.alerts && c.alerts.enabled) ? "opacity-40 pointer-events-none" : ""}`}>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">Alert role to ping</label>
                {linked ? (
                  <select data-testid="alert-role" value={a.role || ""} onChange={(e) => setAlert("role", e.target.value)}
                    className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt">
                    <option value="">— none —</option>
                    <option value="everyone">@everyone</option>
                    <option value="here">@here</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                ) : (
                  <input data-testid="alert-role" value={a.role || ""} onChange={(e) => setAlert("role", e.target.value)} placeholder="role name / everyone"
                    className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
                )}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">Alert channel <span className="text-zinc-600 normal-case tracking-normal">(blank = default)</span></label>
                <ChannelPicker linked={linked} channels={channels} value={a.channel} testid="alert-channel"
                  onChange={(v) => setAlert("channel", v)} placeholder={linked ? `— use default${defaultName ? ` (#${defaultName})` : ""} —` : "channel name"} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">Mass-ban threshold /min</label>
                <input data-testid="alert-massban-threshold" type="number" min={2} value={a.mass_ban_threshold ?? 3} onChange={(e) => setAlert("mass_ban_threshold", Math.max(2, +e.target.value))}
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm outline-none focus:border-volt" />
              </div>
            </div>
            <div className={`space-y-3 ${!(c.alerts && c.alerts.enabled) ? "opacity-40 pointer-events-none" : ""}`}>
              {[["on_mass_ban", "Mass ban", "Many members banned in under a minute (raid / rogue mod)."],
                ["on_role_perms", "Role permission change", "A role's permissions were modified (privilege escalation)."],
                ["on_channel_delete", "Channel deleted", "A channel was deleted."]].map(([k, label, desc]) => (
                <div key={k} className="flex items-center justify-between">
                  <div><div className="text-sm text-zinc-300">{label}</div><div className="text-xs text-zinc-600">{desc}</div></div>
                  <Toggle testid={`alert-${k}`} checked={k === "on_channel_delete" ? !!a[k] : a[k] !== false} onChange={(v) => setAlert(k, v)} />
                </div>
              ))}
              <div className="flex items-center justify-between pt-3 mt-1 border-t border-zinc-800">
                <div><div className="text-sm text-red-300 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Auto-lock on raid</div><div className="text-xs text-zinc-600">On a mass ban, pause invites &amp; set channels read-only. Reverse with <code className="text-volt">/unlock</code>.</div></div>
                <Toggle testid="alert-auto_lock" checked={!!a.auto_lock} onChange={(v) => setAlert("auto_lock", v)} />
              </div>
              <div className={`flex items-center justify-between ${!a.auto_lock ? "opacity-40 pointer-events-none" : ""}`}>
                <div><div className="text-sm text-zinc-300">Auto-unlock after</div><div className="text-xs text-zinc-600">Automatically lift the lockdown after this long (0 = keep locked until <code className="text-volt">/unlock</code>).</div></div>
                <div className="flex items-center gap-2">
                  <input data-testid="alert-auto-unlock-minutes" type="number" min={0} value={a.auto_unlock_minutes ?? 0} onChange={(e) => setAlert("auto_unlock_minutes", Math.max(0, +e.target.value))}
                    className="w-20 bg-black border border-zinc-800 rounded-md px-3 py-1.5 text-sm outline-none focus:border-volt" />
                  <span className="text-xs text-zinc-500">min</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div><div className="text-sm text-red-300 flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Offer one-click raid cleanup</div><div className="text-xs text-zinc-600">Attach a "Ban recent joiners" button to raid alerts. Also available via <code className="text-volt">/raidclean</code>.</div></div>
                <Toggle testid="alert-offer_raid_cleanup" checked={a.offer_raid_cleanup !== false} onChange={(v) => setAlert("offer_raid_cleanup", v)} />
              </div>
              <div className="flex items-center justify-between">
                <div><div className="text-sm text-zinc-300">Raid cleanup window</div><div className="text-xs text-zinc-600">Ban members who joined within this many minutes.</div></div>
                <div className="flex items-center gap-2">
                  <input data-testid="alert-raid-minutes" type="number" min={1} value={a.raid_cleanup_minutes ?? 10} onChange={(e) => setAlert("raid_cleanup_minutes", Math.max(1, +e.target.value))}
                    className="w-20 bg-black border border-zinc-800 rounded-md px-3 py-1.5 text-sm outline-none focus:border-volt" />
                  <span className="text-xs text-zinc-500">min</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
